import { useCallback, useEffect, useState } from "react";
import type {
  ChecklistComponent,
  ChecklistTemplate,
  ComponentType,
  TemplateType,
} from "@/types/components";

export interface TemplateChecklist {
  id?: number;
  name: string;
  description: string;
  service_id: number;
  display_order: number;
  is_required: boolean;
  components: ChecklistComponent[];
}

interface UseTemplateBuilderStateProps {
  templateData: ChecklistTemplate | null | undefined;
  templateId?: number | null;
  services: any[];
}

export function useTemplateBuilderState({
  templateData,
  templateId,
  services,
}: UseTemplateBuilderStateProps) {
  const [template, setTemplate] = useState<ChecklistTemplate | null>(null);
  const [templateChecklists, setTemplateChecklists] = useState<TemplateChecklist[]>([]);
  const [editingChecklist, setEditingChecklist] = useState<TemplateChecklist | null>(null);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [editingComponent, setEditingComponent] = useState<ChecklistComponent | null>(null);
  const [currentChecklistId, setCurrentChecklistId] = useState<number | null>(null);

  // Load template data
  useEffect(() => {
    if (templateData) {
      setTemplate(templateData);
      if (templateData.template_data?.checklists) {
        setTemplateChecklists(templateData.template_data.checklists);
      }
    } else if (!templateId) {
      // New template
      setTemplate({
        id: 0,
        name: "",
        description: "",
        template_type: "bathroom" as TemplateType,
        is_default: false,
        is_active: true,
        display_order: 1,
        template_data: { checklists: [] },
        pricing_data: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setTemplateChecklists([]);
    }
  }, [templateData, templateId]);

  // Checklist management
  const addChecklist = useCallback(() => {
    const newChecklist: TemplateChecklist = {
      name: "New Checklist",
      description: "",
      service_id: services[0]?.id || 1,
      display_order: templateChecklists.length + 1,
      is_required: true,
      components: [],
    };

    setEditingChecklist(newChecklist);
    setShowChecklistDialog(true);
  }, [services, templateChecklists.length]);

  const editChecklist = useCallback((checklist: TemplateChecklist) => {
    setEditingChecklist(checklist);
    setShowChecklistDialog(true);
  }, []);

  const deleteChecklist = useCallback(
    (index: number) => {
      if (confirm("Are you sure you want to delete this checklist?")) {
        const updated = [...templateChecklists];
        updated.splice(index, 1);
        setTemplateChecklists(updated);
      }
    },
    [templateChecklists]
  );

  const moveChecklist = useCallback(
    (index: number, direction: "up" | "down") => {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= templateChecklists.length) return;

      const updated = [...templateChecklists];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

      // Update display orders
      updated.forEach((checklist, idx) => {
        checklist.display_order = idx + 1;
      });

      setTemplateChecklists(updated);
    },
    [templateChecklists]
  );

  const saveChecklist = useCallback(
    (checklist: TemplateChecklist) => {
      const existingIndex = templateChecklists.findIndex(
        (c) => c.id === checklist.id || (c.name === editingChecklist?.name && !checklist.id)
      );

      if (existingIndex >= 0) {
        const updated = [...templateChecklists];
        updated[existingIndex] = { ...checklist, display_order: existingIndex + 1 };
        setTemplateChecklists(updated);
      } else {
        setTemplateChecklists((prev) => [
          ...prev,
          { ...checklist, display_order: prev.length + 1 },
        ]);
      }

      setEditingChecklist(null);
      setShowChecklistDialog(false);
    },
    [templateChecklists, editingChecklist]
  );

  // Component management
  const addComponent = useCallback(
    (checklistIndex: number) => {
      const newComponent: ChecklistComponent = {
        checklist_id: 0,
        component_type: "text" as ComponentType,
        field_name: `field_${Date.now()}`,
        label: "New Component",
        is_required: false,
        display_order: templateChecklists[checklistIndex].components.length + 1,
        base_price: 0,
        pricing_type: "fixed",
        options: [],
      };

      setEditingComponent(newComponent);
      setCurrentChecklistId(checklistIndex);
      setShowComponentDialog(true);
    },
    [templateChecklists]
  );

  const editComponent = useCallback(
    (checklistIndex: number, componentIndex: number) => {
      const component = templateChecklists[checklistIndex].components[componentIndex];
      setEditingComponent(component);
      setCurrentChecklistId(checklistIndex);
      setShowComponentDialog(true);
    },
    [templateChecklists]
  );

  const saveComponent = useCallback(
    (component: ChecklistComponent) => {
      if (currentChecklistId === null) return;

      const updated = [...templateChecklists];
      const checklist = updated[currentChecklistId];

      const existingIndex = checklist.components.findIndex(
        (c) => c.field_name === component.field_name
      );

      if (existingIndex >= 0) {
        checklist.components[existingIndex] = component;
      } else {
        checklist.components.push(component);
      }

      // Update display orders
      checklist.components.forEach((comp, idx) => {
        comp.display_order = idx + 1;
      });

      setTemplateChecklists(updated);
      setEditingComponent(null);
      setCurrentChecklistId(null);
      setShowComponentDialog(false);
    },
    [templateChecklists, currentChecklistId]
  );

  const deleteComponent = useCallback(
    (checklistIndex: number, componentIndex: number) => {
      if (confirm("Are you sure you want to delete this component?")) {
        const updated = [...templateChecklists];
        updated[checklistIndex].components.splice(componentIndex, 1);
        setTemplateChecklists(updated);
      }
    },
    [templateChecklists]
  );

  return {
    // State
    template,
    templateChecklists,
    editingChecklist,
    showChecklistDialog,
    showComponentDialog,
    editingComponent,
    currentChecklistId,

    // Setters
    setTemplate,
    setTemplateChecklists,
    setEditingChecklist,
    setShowChecklistDialog,
    setShowComponentDialog,
    setEditingComponent,
    setCurrentChecklistId,

    // Checklist operations
    addChecklist,
    editChecklist,
    deleteChecklist,
    moveChecklist,
    saveChecklist,

    // Component operations
    addComponent,
    editComponent,
    saveComponent,
    deleteComponent,
  };
}
